import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import FeatureFlagsContext from './feature-flags-context';
import useIdentificationSettlement from './identification-settlement-hook';
import type { FeatureFlags, FeatureFlagsContextProps } from './types';

type Props = {
  children: ReactNode;
};

const FeatureFlagsProvider = ({ children }: Props) => {
  const rawFlags = useFlags<FeatureFlags>();
  const ldClient = useLDClient();
  const {
    identificationStatus,
    identificationContextKey,
    settleIdentification
  } = useIdentificationSettlement();

  const featureFlags: FeatureFlags = useMemo<FeatureFlags>(
    () => ({
      releaseWebNewFieldAllocationTableView:
        rawFlags.releaseWebNewFieldAllocationTableView,
      releaseWebAgronomicDemandHybridFeature:
        rawFlags.releaseWebAgronomicDemandHybridFeature,
      releaseWebParentSeedsAssignmentHybrid:
        rawFlags.releaseWebParentSeedsAssignmentHybrid,
      releaseWebParentSeedsBatchInventoryStatus:
        rawFlags.releaseWebParentSeedsBatchInventoryStatus,
      releaseWebExportBatchesHybrid: rawFlags.releaseWebExportBatchesHybrid,
      releaseWebExportBatchesPoNumber: rawFlags.releaseWebExportBatchesPoNumber,
      releaseWebPaginatedAllocationsTable:
        rawFlags.releaseWebPaginatedAllocationsTable,
      releaseWebDataImport: rawFlags.releaseWebDataImport,
      releaseWebSeasonVigorGermination:
        rawFlags.releaseWebSeasonVigorGermination,
      releaseWebDiscardedAllocationsVisible:
        rawFlags.releaseWebDiscardedAllocationsVisible,
      releaseWebVarietyAllocationDeleteResponse:
        rawFlags.releaseWebVarietyAllocationDeleteResponse,
      releaseWebBulkEditIsolationAreas:
        rawFlags.releaseWebBulkEditIsolationAreas,
      releaseWebCreateIsolationArea: rawFlags.releaseWebCreateIsolationArea,
      releaseWebAgronomicDemandPlaceholderRows:
        rawFlags.releaseWebAgronomicDemandPlaceholderRows,
      releaseWebAgronomicDemandHybridFilters:
        rawFlags.releaseWebAgronomicDemandHybridFilters,
      releaseWebAgronomicDemandHybridAreas:
        rawFlags.releaseWebAgronomicDemandHybridAreas,
      releaseWebReviewSppData: rawFlags.releaseWebReviewSppData,
      releaseWebAllocationDrawerUoms: rawFlags.releaseWebAllocationDrawerUoms,
      releaseWebSeasonFieldsListView: rawFlags.releaseWebSeasonFieldsListView,
      releaseWebMalePlantingDate2: rawFlags.releaseWebMalePlantingDate2,
      releaseWebPrefilledAreaVolume: rawFlags.releaseWebPrefilledAreaVolume,
      releaseWebProductionOrderMaterialGroup:
        rawFlags.releaseWebProductionOrderMaterialGroup,
      releaseWebAllocationEditRules: rawFlags.releaseWebAllocationEditRules,
      releaseWebPaginatedProductionInstructions:
        rawFlags.releaseWebPaginatedProductionInstructions,
      releaseWebFarmProtectorIntegration:
        rawFlags.releaseWebFarmProtectorIntegration,
      releaseWebGrowerManagementListRedesign:
        rawFlags.releaseWebGrowerManagementListRedesign,
      releaseWebAllocationPoIntegrationColumns:
        rawFlags.releaseWebAllocationPoIntegrationColumns,
      releaseWebAllocationCustomFields:
        rawFlags.releaseWebAllocationCustomFields,
      releaseWebProductionOrderRatioRules:
        rawFlags.releaseWebProductionOrderRatioRules,
      releaseWebAllocationFieldHierarchySearch:
        rawFlags.releaseWebAllocationFieldHierarchySearch,
      releaseWebPlanningRegionsImport: rawFlags.releaseWebPlanningRegionsImport,
      releaseWebPoChangedColumns: rawFlags.releaseWebPoChangedColumns,
      releaseWebCycleSpecificPiTemplateDownload:
        rawFlags.releaseWebCycleSpecificPiTemplateDownload,
      releaseWebGrowingAreasNamesFilter:
        rawFlags.releaseWebGrowingAreasNamesFilter,
      releaseWebGrowerDrawerRefresh: rawFlags.releaseWebGrowerDrawerRefresh,
      releaseWebTopbarCropSeasonSelection:
        rawFlags.releaseWebTopbarCropSeasonSelection,
      releaseWebTopbarSearchTreeSelectors:
        rawFlags.releaseWebTopbarSearchTreeSelectors,
      releaseWebEnvironmentProductName:
        rawFlags.releaseWebEnvironmentProductName,
      // Configuration flags
      configureWebDatadogSessionSampleRate:
        rawFlags.configureWebDatadogSessionSampleRate,
      configureWebDatadogSessionReplaySampleRate:
        rawFlags.configureWebDatadogSessionReplaySampleRate,
      configureWebProductionInstructionsTemplate:
        rawFlags.configureWebProductionInstructionsTemplate,
      configureWebCropwiseIntegrationsScreen:
        rawFlags.configureWebCropwiseIntegrationsScreen
    }),
    [rawFlags]
  );

  const value = useMemo<FeatureFlagsContextProps>(
    () => ({
      flags: featureFlags,
      isEnabled: <K extends keyof FeatureFlags>(flag: K) =>
        Boolean(featureFlags[flag]),
      ldClient,
      identificationStatus,
      identificationContextKey,
      settleIdentification
    }),
    [
      featureFlags,
      ldClient,
      identificationStatus,
      identificationContextKey,
      settleIdentification
    ]
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export default FeatureFlagsProvider;
